import { useState, useEffect, useCallback } from 'react';
import moment from 'moment';
import { RequestTrackingStatisticsResponse } from '@openapi';
import api from '@services/api';
import { TimeRange, computeDateRange } from './useTimeRange';

interface UseStatisticsResult {
  statistics: RequestTrackingStatisticsResponse | null;
  errorDistribution: Record<string, number>;
  loading: boolean;
  refetch: () => void;
}

export const useStatistics = (
  timeRange: TimeRange,
  customDateRange: [moment.Moment, moment.Moment] | null,
): UseStatisticsResult => {
  const [statistics, setStatistics] = useState<RequestTrackingStatisticsResponse | null>(null);
  const [errorDistribution, setErrorDistribution] = useState<Record<string, number>>({});
  const [loading, setLoading] = useState<boolean>(false);

  const fetchData = useCallback(async () => {
    const dateRange = computeDateRange(timeRange, customDateRange);
    if (!dateRange) return;

    setLoading(true);
    try {
      let statsPromise: Promise<RequestTrackingStatisticsResponse>;
      if (timeRange === 'daily') {
        statsPromise = api.tracking.getDailyStatistics();
      } else if (timeRange === 'weekly') {
        statsPromise = api.tracking.getWeeklyStatistics();
      } else if (timeRange === 'monthly') {
        statsPromise = api.tracking.getMonthlyStatistics();
      } else {
        statsPromise = api.tracking.getStatistics({
          startTime: dateRange.startTime,
          endTime: dateRange.endTime,
        });
      }

      const errorDistPromise = api.tracking.getErrorDistribution({
        startTime: dateRange.startTime,
        endTime: dateRange.endTime,
      });

      const [statsData, errorDistData] = await Promise.all([statsPromise, errorDistPromise]);
      setStatistics(statsData);
      setErrorDistribution(errorDistData || {});
    } catch (error) {
      console.error('Failed to fetch statistics:', error);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timeRange, customDateRange]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return { statistics, errorDistribution, loading, refetch: fetchData };
};
